import { useState, useEffect, useMemo } from 'react';
import { Button, Card, Nav, Spinner } from 'react-bootstrap';
import { dynamicTranslateOpportunityFields } from '../../../utils/opportunity'
import { Storage } from 'aws-amplify';
import { FaInfoCircle } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import _ from 'lodash';
import { randomString, strToFloat } from '../../../utils';
import { Markets, OpportunityFunnelValues, ReactSelectOption } from '../../../types';
import { MotornetModelSummary } from '../../../motornet/types';
import { getMakeName } from '../../../motornet/common/MotornetVehicleSelector';
import { useApiClient } from '../../../hooks/useApiClient';
import { AccountEntitySearch, OpportunityField } from '../../../api/models/AccountEntity';
import { VehicleEntity, OpportunityCreateRequestDTO, VehicleUpdateRequestDTO } from '../../../api';
import { OPPORTUNITY_CREATION_LEAD_SOURCE, OPPORTUNITY_STATES_DATA } from '../../../utils/constants';
import { OpportunityStageEnum, RoleEnum } from "../../../api/models/Enums";
import CardPlaceholder from '../../CardPlaceholder/CardPlaceholder';
import OpportunityModals from '../OpportunityModals/OpportunityModals';
import { useAuth } from '../../../hooks/useAuth';
// @ts-ignore
import './OpportunityFunnelV2.scss';
import { createVehicleSearch } from 'src/vehicleList/vehicleSearch';
import { translateOpportunityState } from '../../../utils/translations';
import { isMarket } from 'src/utils/markets';
import { getDocumentsCountLabel, getExternalVehicleLabel, getVehicleLabel, tabsByStage, toUpperCase } from './utils';
import { OpportunityFunnelTab, OpportunityPanel, SelectVehiclePanel, SendDocumentsPanel, UploadDocumentsPanel, UploadSendDocumentsPanel, AccountPanel } from './components';
import { FunnelStep, UploadFile } from './types';
import { useErrorToast } from 'src/hooks/useErrorToast';
import { VALIDATION } from './validation';
import * as Yup from 'yup';
import { getOpportunityCreationValidationSchema } from '../OpportunityCreation/OpportunityCreation';



type SendDataState = {
	enabled: boolean
	message: string
	to: ReactSelectOption | null
	dealer: ReactSelectOption | null
	dealerDocs: string[]
	cc: ReactSelectOption[]
}


interface OpportunityFunnelProps {
	account: AccountEntitySearch
	mustComplete: FunnelStep[]
	saveLabel?: string
	nextState?: OpportunityStageEnum
	onQuit?: () => void
	afterSubmitFn: (opportunityId: string) => void
}

interface FunnelDataState {
	account?: AccountEntitySearch
	vehicle?: VehicleEntity
	vehicleSource: string
	motornetSelectedVehicle: {
		brand: ReactSelectOption | null,
		model: (ReactSelectOption & { data?: MotornetModelSummary }) | null,
		images: string[]
	}
}

type FunnelValidationState = {
	account: boolean
	vehicle: boolean
	upload_docs: boolean
	send_docs: boolean
	opportunity: boolean
	send_or_upload_docs: boolean
}

const OPPORTUNITY_INITIAL_VALUES: OpportunityFunnelValues = {
	entity: null,
	Tipologia__c: null,
	Canale__c: null,
	Noleggiatore__c: null,
	commissions: '',
	notes: '',
	name: '',
	driveItEasy: false,
	driverEmail: "",
	targa: "",
	telaio: "",
	dataFineContrattoDriveItEasy: "",
	nomePartner: null,
	LeadSource: null,
}

export const OpportunityFunnelV2 = ({ account, onQuit, afterSubmitFn }: OpportunityFunnelProps) => {
	const { userRole, userReferencePartnerList } = useAuth();
	const opportunityCreationSchema = getOpportunityCreationValidationSchema(userRole).concat(Yup.object().shape({
		LeadSource: Yup.object().required(i18next.t('required_field')),
	}));
	const [funnelData, setFunnelData] = useState<FunnelDataState>({
		account,
		vehicle: undefined,
		vehicleSource: 'hmp',
		motornetSelectedVehicle: { model: null, brand: null, images: [] }
	})

	const { t } = useTranslation()
	const [documentationFolder, setDocumentationFolder] = useState<ReactSelectOption[]>([]);
	const { apiClient, handleApiResponse } = useApiClient();
	const [loading, setLoading] = useState(true);
	const [sending, setSending] = useState(false)
	const [sendFiles, setSendFiles] = useState<File[]>([])
	const [modalType, setModalType] = useState('');
	const [sendData, setSendData] = useState<SendDataState>({
		to: null,
		enabled: true,
		dealer: null,
		cc: [],
		message: i18next.t('rental_application_instructions_email_template'),
		dealerDocs: []
	})
	const { showError } = useErrorToast();
	const [sendEmailData, setSendEmailData] = useState<any>({
		enabled: false,
		message: '',
		to: undefined,
		dealer: null,
		dealerDocs: undefined,
		cc: [],
		files: []
	});

	const [dealerFolderData, setDealerFolderData] = useState<any[]>([])
	const [canSave, setCanSave] = useState<boolean>(false)

	const [validation, setValidation] = useState<FunnelValidationState>({
		account: false,
		vehicle: false,
		upload_docs: false,
		send_docs: false,
		opportunity: false,
		send_or_upload_docs: false
	})

	const [uploadFiles, setUploadFiles] = useState<File[]>([])
	const [accountModalOpened, setAccountModalOpened] = useState<boolean>(false)
	const [activeKey, setActiveKey] = useState<FunnelStep>('account')
	const [vehicleSearchOpt, setVehicleSearchOpt] = useState<ReactSelectOption | null>(null)
	const [opportunityFields, setOpportunityFields] = useState<OpportunityField[]>([])
	const [activateDriveItEasy, setActivateDriveItEasy] = useState<boolean>(false)
	const [opportunityValues, setOpportunityValues] = useState<OpportunityFunnelValues>({ ...OPPORTUNITY_INITIAL_VALUES })
	const [selectedOpportunityStage, setSelectedOpportunityStage] = useState<OpportunityStageEnum>(OpportunityStageEnum.proposal);

	const canChange = useMemo(() => {
		const allowedTabs = tabsByStage(selectedOpportunityStage);
		console.debug('[hmp.opportunity.create] allowed tabs ', allowedTabs)
		return allowedTabs
	}, [selectedOpportunityStage])

	const resetData = () => {
		setUploadFiles([])
		setSendFiles([])
		setSendData({
			to: null,
			enabled: true,
			dealer: null,
			cc: [],
			message: i18next.t('rental_application_instructions_email_template'),
			dealerDocs: []
		})

		setSendEmailData({
			enabled: false,
			message: '',
			to: null,
			dealer: null,
			dealerDocs: [],
			cc: [],
			files: []
		})
		setDealerFolderData([])
	}

	useEffect(() => {
		const nomePartner = userReferencePartnerList.length > 1
			? null
			: { active: true, label: userReferencePartnerList[0], value: userReferencePartnerList[0] };

		setOpportunityValues((prevValues) => ({
			...prevValues,
			driverEmail: (account?.RecordType?.Name === 'Privato' || account?.RecordType?.Name === 'Libero Professionista')
				? account?.Email__c
				: "",
			nomePartner
		}));
	}, [account, userReferencePartnerList])

	useEffect(() => {
		if (funnelData.account && funnelData.account.Email__c) {
			setSendData({
				...sendData,
				to: {
					value: funnelData.account.Email__c,
					label: funnelData.account.Email__c
				}
			})
		}
		setCanSave(validateFn())
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [funnelData])

	useEffect(() => {
		setCanSave(validateFn())
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sendData, uploadFiles, funnelData.motornetSelectedVehicle, opportunityValues])

	const isSendDataValid = () => {
		return sendData.enabled
			? (sendData.to !== null && sendData.message.length > 0 && sendData.dealerDocs.length > 0)
			: true
	}

	const isUploadSendDocRequired = useMemo(() => {
		if (!canChange.includes('send_or_upload_docs')) {
			return false;
		}
		if (selectedOpportunityStage === OpportunityStageEnum.proposal) {
			return false;
		}

		return true;
	}, [selectedOpportunityStage, canChange])

	const isUploadSendDocsValid = () => {
		if (selectedOpportunityStage === OpportunityStageEnum.proposal) {
			return true;
		}

		return VALIDATION.uploadSendDocsSchema.isValidSync(sendEmailData)
	}

	const isUploadDocsRequired = () => {
		if (!canChange.includes('upload_docs')) {
			return false;
		}

		if (selectedOpportunityStage === OpportunityStageEnum.credit_score_check) {
			return false;
		}

		return true;
	}

	const validateFn = () => {
		const accountStatus = funnelData.account !== undefined;
		const vehicleStatus = (funnelData.vehicle !== undefined || funnelData.motornetSelectedVehicle.model !== null);
		const uploadStatus = isUploadDocsRequired() ? uploadFiles.length > 0 : true;
		const sendDocsStatus = canChange.includes('send_docs') ? isSendDataValid() : true;
		const opportunityStatus = opportunityCreationSchema.isValidSync(opportunityValues);
		const uploadSendDocsStatus = canChange.includes('send_or_upload_docs') ? isUploadSendDocsValid() : true;

		const validation = {
			account: accountStatus,
			vehicle: vehicleStatus,
			upload_docs: uploadStatus,
			send_docs: sendDocsStatus,
			opportunity: opportunityStatus,
			send_or_upload_docs: uploadSendDocsStatus
		}

		console.debug({ validation, sendEmailData, sendDocsStatus, sendData, opportunityValues })

		setValidation(validation)

		let result = accountStatus && vehicleStatus && sendDocsStatus && uploadStatus && opportunityStatus && uploadSendDocsStatus;

		if (canChange.length > 1 && !canChange.includes('send_docs') && !canChange.includes('upload_docs')) {
			result = result && (sendData.enabled || uploadFiles.length > 0)
		}

		return result
	};

	const onOpportunityFieldsFetched = (opportunityFieldsRes) => {
		let accTyps = dynamicTranslateOpportunityFields(opportunityFieldsRes)
		accTyps.push(
			{
				label: t('dealer'),
				name: 'nomePartner',
				picklistValues: userReferencePartnerList.map(value => ({
					active: true, label: value, value: value
				}))
			}
		)
		accTyps = accTyps.map(field => {
			if (field.name?.toLowerCase() === 'canale__c') {
				const hideSegnalatore = [RoleEnum.SALES_DEALER, RoleEnum.SALES_INTERNAL].includes(userRole) || isMarket(Markets.es);

				if (hideSegnalatore) {
					field.picklistValues = field.picklistValues.filter(
						item => item.value?.toLowerCase() !== 'segnalatore'
					);
				}
			}
			return field;
		})

		const edisonIdx = accTyps?.findIndex(field => field?.name === 'Offerta_Edison__c');

		if (![null, undefined, -1].includes(edisonIdx)) {
			const [edisonField] = accTyps.splice(edisonIdx, 1);
			edisonField && isMarket(Markets.it) && accTyps.push(edisonField);
		}

		setOpportunityFields(accTyps)
	}

	const loadData = async () => {
		// fetch opportunity fields
		handleApiResponse(apiClient.salesforceOpportunityController.getOpportunityFields())
			.then(onOpportunityFieldsFetched)

		setLoading(false)
	}

	useEffect(() => {
		// Fetch all documents from the folder Noleggiatori/Documentazione from area academy
		findAndSetNoleggiatoriDocuments();

		if (account) {
			setFunnelData({ ...funnelData, account })
		}

		if (canChange.includes('send_docs')) {
			setSendData({ ...sendData, enabled: true })
		}

		loadData()
	}, []);


	const loadDealerDocs = async (opt) => {
		if (!funnelData.account) return;

		const folderId = opt.value

		setDealerFolderData([])
		setSendData({ ...sendData, dealer: opt, dealerDocs: [] })

		const folderData = await handleApiResponse(apiClient.areaAcademyController.getSingleFolder(folderId))
		let dealerFolderData = folderData.data.filter(el => el.type === 'file');

		const accountType = funnelData.account.RecordType.Name;
		let accountTypeToSearch: { [key: string]: string[] } = {
			'Libero Professionista': toUpperCase(['liberi professionisti/società di persone']),
			'Privato': toUpperCase(['Privati']),
			'Azienda': toUpperCase(['aziende (società di capitali)']),
		};

		const accountTypeFolder = folderData.data.find(el => el.type === 'folder' && accountTypeToSearch[accountType]?.includes(el.title.toUpperCase()))

		if (accountTypeFolder) {
			const accountTypeFolderContent = await handleApiResponse(apiClient.areaAcademyController.getSingleFolder(accountTypeFolder.id))
			const accountTypeFolderData = accountTypeFolderContent.data.filter(el => el.type === 'file')
			dealerFolderData = [...dealerFolderData, ...accountTypeFolderData]
		}

		setDealerFolderData(dealerFolderData)
		setSendData({ ...sendData, dealer: opt, dealerDocs: dealerFolderData.map(el => el.title) })
	}

	const findAndSetNoleggiatoriDocuments = async () => {
		const translations = {
			noleggiatori: ['NOLEGGIATORI', 'RENTAL OPERATORS', 'AGENTES DE ALQUILER'],
			documentazione: ['DOCUMENTAZIONE', 'DOCUMENTATION', 'DOCUMENTACIÓN']
		};

		const findFolderByName = (folders, possibleNames) =>
			folders.find(el => possibleNames.includes(el.title.toUpperCase()));

		if (documentationFolder.length === 0) {
			try {
				const res = await handleApiResponse(apiClient.areaAcademyController.getRootAcademyFolder());

				// Find Noleggiatori folder dynamically
				const noleggiatoriFolder = findFolderByName(res.data, translations.noleggiatori);

				if (noleggiatoriFolder) {
					const noleggiatoriFolderRes = await handleApiResponse(apiClient.areaAcademyController.getSingleFolder(noleggiatoriFolder.id));


					// Find Documentazione folder dynamically
					const docFolder = findFolderByName(noleggiatoriFolderRes.data, translations.documentazione);

					if (docFolder) {
						const nDocFolderRes = await handleApiResponse(apiClient.areaAcademyController.getSingleFolder(docFolder.id));


						setDocumentationFolder(nDocFolderRes.data
							.filter(el => el.type === 'folder')
							.map(el => ({
								label: el.title,
								value: el.id
							})));
					}
				}
			} catch (err) {
				console.error(err);
			}
		}
	};

	const vehicleSearch = useMemo(
		() => createVehicleSearch((params) => apiClient.motornetControllerService.getPlatformVehicles(params)),
		[apiClient]
	);

	const uploadFilesToBucket = async ({
		sendFiles,
		uploadFiles,
	}) => {
		let filesToUploadList: UploadFile[] = []

		const uploadContent = {
			sendFiles,
			uploadFiles,
		}

		for (const type in uploadContent) {
			const fileArray: { data: File[], type: 'sendFiles' | 'uploadFiles' } = uploadContent[type]

			if (!fileArray.data.length) {
				continue;
			}

			let uploadOps: any[] = []

			fileArray.data.forEach(file => {
				const ext = file.name.split('.').pop()
				const key = `opportunity_uploads/${randomString(10)}.${ext}`
				filesToUploadList.push({ type, file, filename: file.name, key })
			})

			uploadOps = filesToUploadList.map(obj => {
				return Storage.put(obj.key, obj.file, {
					contentType: obj.file.type,
					bucket: process.env.REACT_APP_AWS_STORAGE_BUCKET_SALESFORCE_IMPORT,
				});
			})

			try {
				await Promise.all(uploadOps)
			} catch (e) {
				console.error(e);
				showError(e, i18next.t('error_loading_file'))
				throw e;
			}
		}

		return filesToUploadList.map(el => ({ ...el, key: `public/${el.key}` }))
	}

	const hideModal = () => {
		setAccountModalOpened(false)

		if (activeKey === 'opportunity' && validation.opportunity && opportunityValues.Noleggiatore__c) {
			const docFolder = documentationFolder.find(f => f.label === opportunityValues.Noleggiatore__c?.value)

			if (docFolder) {
				loadDealerDocs(docFolder)
			} else {
				setDealerFolderData([])
				setSendData({ ...sendData, dealer: null, dealerDocs: [] })
			}
		}
	};

	const getCommissionsField = () => {
		if ([RoleEnum.MASTER, RoleEnum.BACKOFFICE, RoleEnum.SALES_INTERNAL].includes(userRole)) {
			return 'commissions';
		}

		if ([RoleEnum.AREA_MANAGER, RoleEnum.SALES_DEALER].includes(userRole)) {
			return 'provvigioniPassive';
		}

		return 'commissions';
	};

	const submitFn = async () => {

		if (!validateFn()) return;
		setSending(true);

		let filesToUploadList = await uploadFilesToBucket({
			sendFiles: canChange.includes('send_docs') ? { data: sendFiles } : { data: [] },
			uploadFiles: { data: uploadFiles },
		})

		const isMasterOrBackoffice = [RoleEnum.MASTER, RoleEnum.BACKOFFICE].includes(userRole);
		const isSegnalazione = opportunityValues?.Canale__c?.value?.toLowerCase() === 'segnalatore';
		const commissionsField = getCommissionsField();

		const provvPayload = isMasterOrBackoffice
			? {
				commissions: opportunityValues?.commissions
					? strToFloat(opportunityValues.commissions)
					: 0,
				provvigioniPassive: opportunityValues?.provvigioniPassive
					? strToFloat(opportunityValues.provvigioniPassive)
					: undefined,
			  }
			: { [commissionsField]: strToFloat(opportunityValues?.commissions) };

		// @ts-ignore
		let createOpportunityPayload: OpportunityCreateRequestDTO = {
			accountId: funnelData.account!.Id,
			name: `HMP-${funnelData.account!.Name}-${opportunityValues?.name}`,
			...provvPayload,
			provvigioniSegnalatore: isSegnalazione && opportunityValues?.provvigioniSegnalatore
				? strToFloat(opportunityValues.provvigioniSegnalatore)
				: undefined,
			segnalatore: isSegnalazione ? (opportunityValues?.segnalatore || undefined) : undefined,
			Ragione_Sociale_Segnalatore__c: isSegnalazione ? (opportunityValues?.ragioneSocialeSegnalatore || undefined) : undefined,
			channel: opportunityValues?.Canale__c?.value,
			type: opportunityValues?.Tipologia__c?.value,
			dealerName: opportunityValues?.Noleggiatore__c?.value,
			emailSending: null,
			signedAttachments: null,
			note: opportunityValues?.notes,
			stageName: selectedOpportunityStage,
			vehicle: {
				vehicleId: null,
				customVehicle: null
			},
			driveItEasy: opportunityValues?.driveItEasy,
			driverEmail: opportunityValues?.driverEmail,
			targa: opportunityValues?.targa,
			telaio: opportunityValues?.telaio,
			isConvertedFromLead__c: false,
			nomePartner: opportunityValues?.nomePartner?.value,
			Offerta_Edison__c: opportunityValues?.Offerta_Edison__c?.value,
			salesUser: opportunityValues?.salesUser || '',
			referenceUserId: opportunityValues?.referenceUserId || '',
			backupUsers: opportunityValues?.backupUsers || [],
			LeadSource: opportunityValues?.LeadSource?.value,
		}

		/* SOLO PER HMP ITALIA */
		if (isMarket(Markets.es)) {
			createOpportunityPayload = _.omit(createOpportunityPayload, ['Offerta_Edison__c'])
		}

		// payload.vehicle
		if (funnelData.vehicleSource === 'external') {
			createOpportunityPayload.vehicle!.customVehicle = {
				brand: getMakeName(funnelData.motornetSelectedVehicle.brand),
				model: funnelData.motornetSelectedVehicle.model?.data?.gammaModello?.descrizione || funnelData.motornetSelectedVehicle.model!.label,
			};
		} else if (funnelData.vehicle) {
			createOpportunityPayload.vehicle!.vehicleId = funnelData.vehicle.id?.toString() || null;
		}

		// emailSending
		if (canChange.includes('send_docs')) {
			if (sendData.enabled) {
				createOpportunityPayload.emailSending = {
					"to": sendData.to!.value,
					"cc": sendData.cc.map(el => el.value),
					"body": sendData.message,
					"existingAttachments": sendData.dealerDocs.map(el => ({ key: 'public/' + el, filename: el })),
					"uploadedAttachments": filesToUploadList.filter(el => el.type === 'sendFiles').map(item => ({
						"filename": item.filename,
						"key": item.key
					})
					),
				}
			}
		}

		if (canChange.includes('send_or_upload_docs')) {
			const attachments = filesToUploadList.filter(el => el.type === 'uploadFiles').map(item => ({
				"filename": item.filename,
				"key": item.key
			}));

			const to = _.isString(sendEmailData?.to)
				? sendEmailData?.to
				// @ts-ignore
				: sendEmailData?.to?.value;

			if (sendEmailData.enabled && to) {
				createOpportunityPayload.emailSending = {
					"to": to,
					"cc": sendEmailData?.cc?.map(el => el.value).filter((item): item is string => Boolean(item)) || [],
					"body": sendEmailData?.message,
					"uploadedAttachments": attachments,
				}
			}
			createOpportunityPayload.signedAttachments = attachments
		}

		// upload docs
		if (canChange.includes('upload_docs') && uploadFiles.length) {
			createOpportunityPayload.signedAttachments = filesToUploadList.filter(el => el.type === 'uploadFiles').map(item => ({
				"filename": item.filename,
				"key": item.key
			}))
		}

		// UPDATE VEHICLE HMP only if targa or telatio or dataFineContrattoDriveItEasy are changed by user input
		if (
			funnelData.vehicle?.id && (
				opportunityValues.targa !== funnelData.vehicle?.dettagli_veicolo?.targa ||
				opportunityValues.telaio !== funnelData.vehicle?.dettagli_veicolo?.telaio ||
				opportunityValues.dataFineContrattoDriveItEasy !== funnelData.vehicle?.stato_corrente?.dataFineContrattoDriveItEasy
			)
		) {
			// Update only the fields that changed
			const updatedStatoCorrente = {
				...funnelData.vehicle?.stato_corrente,
				dataFineContrattoDriveItEasy: opportunityValues.dataFineContrattoDriveItEasy || funnelData.vehicle?.stato_corrente?.dataFineContrattoDriveItEasy,
			};

			const updateVehiclePayload: VehicleUpdateRequestDTO = {
				// Include all fields from the DTO with existing values
				id_bo: funnelData.vehicle?.dettagli_veicolo?.id_bo,
				city: funnelData.vehicle?.dettagli_veicolo?.city,
				colore_esterno: funnelData.vehicle?.dettagli_veicolo?.colore_esterno,
				colore_interno: funnelData.vehicle?.dettagli_veicolo?.colore_interno,
				data_disp: funnelData.vehicle?.dettagli_veicolo?.data_disp,
				data_immatricolazione: funnelData.vehicle?.dettagli_veicolo?.data_immatricolazione,
				data_telaio: funnelData.vehicle?.dettagli_veicolo?.data_telaio,
				giorno_disp: funnelData.vehicle?.dettagli_veicolo?.giorno_disp,
				note: funnelData.vehicle?.dettagli_veicolo?.note,
				optionals: funnelData.vehicle?.optionals,
				promo: funnelData.vehicle?.dettagli_veicolo?.promo,
				promo_note: funnelData.vehicle?.dettagli_veicolo?.promo_note,
				settimana_prod: funnelData.vehicle?.dettagli_veicolo?.settimana_prod,
				targa: opportunityValues.targa || funnelData.vehicle?.dettagli_veicolo?.targa,
				telaio: opportunityValues.telaio || funnelData.vehicle?.dettagli_veicolo?.telaio,
				document: funnelData.vehicle?.dettagli_veicolo?.document,
				stato_corrente: updatedStatoCorrente,
				prezzo_listino: funnelData.vehicle?.dati_veicolo?.prezzo_listino,
				in_evidenza: funnelData.vehicle?.dettagli_veicolo?.in_evidenza,
				lista_noleggiatori_quotatore: funnelData.vehicle?.lista_noleggiatori_quotatore,
				extra_sconto_perc: funnelData.vehicle?.dati_veicolo?.extra_sconto_perc,
				limited_visibility: funnelData.vehicle?.dettagli_veicolo?.limited_visibility || [],
				// Mandatory fields
				codice_costruttore: funnelData.vehicle?.dettagli_veicolo?.codice_costruttore ?? '',
				codice_colore_esterno: funnelData.vehicle?.dettagli_veicolo?.codice_colore_esterno ?? '',
				codice_colore_interno: funnelData.vehicle?.dettagli_veicolo?.codice_colore_interno ?? '',
				dealer: funnelData.vehicle?.dettagli_veicolo?.dealer ?? '',
				nr_ordine: funnelData.vehicle?.dettagli_veicolo?.nr_ordine ?? '',
			};


			try {
				await handleApiResponse(apiClient.vehicleController.putVehicles(funnelData.vehicle?.id as string, updateVehiclePayload))
			} catch (err) {
				console.error(err)
				showError(err, i18next.t('error_updating_vehicle'))
			}
		}

		// CREATE OPPORTUNITY
		try {
			const res = await handleApiResponse(apiClient.salesforceOpportunityController.createOpportunity(createOpportunityPayload))

			if (afterSubmitFn) {
				afterSubmitFn(res.id)
			}

		} catch (err) {
			console.error(err)
			showError(err, i18next.t('error_creating_opportunity'));
		} finally {
			setSending(false)
		}
	};

	const cleanOpportunityValues = () => {
		setOpportunityValues({ ...OPPORTUNITY_INITIAL_VALUES })
	}

	const vehicleTabLabel = useMemo(() => {
		return funnelData.vehicle
			? getVehicleLabel(funnelData.vehicle)
			: getExternalVehicleLabel(funnelData.motornetSelectedVehicle, 'Seleziona un veicolo');
	}, [funnelData.motornetSelectedVehicle, funnelData.vehicle])

	return (
		<div>
			<Nav
				fill variant="tabs"
				activeKey={activeKey}
				onSelect={(k) => {
					setActiveKey(k as FunnelStep || i18next.t('account'))
				}}>
				<OpportunityFunnelTab
					eventKey="account"
					title={i18next.t('account')}
					isValid={validation.account}
					mustComplete={canChange}
					text={funnelData.account?.Name || i18next.t('select_one_account')}
				/>
				<OpportunityFunnelTab
					eventKey="opportunity"
					title={i18next.t('opportunity_data')}
					isValid={validation.opportunity}
					mustComplete={canChange}
					text={validation.opportunity ? i18next.t('completed') : i18next.t('complete_form')}
				/>
				<OpportunityFunnelTab
					eventKey="vehicle"
					title={i18next.t('vehicle')}
					isValid={validation.vehicle}
					mustComplete={canChange}
					text={vehicleTabLabel}
				/>
				{canChange.includes('send_docs') && (
					<OpportunityFunnelTab
						eventKey="send_docs"
						title={i18next.t('send_quote_docs')}
						forceRequired={canChange.includes('send_docs') && !(uploadFiles.length > 0 && validation.upload_docs)}
						mustComplete={canChange}
						isValid={sendData.enabled ? validation.send_docs : false}
						text={getDocumentsCountLabel([...sendFiles, ...sendData.dealerDocs])}
					/>
				)}
				{canChange.includes('upload_docs') && (
					<OpportunityFunnelTab
						eventKey="upload_docs"
						title={i18next.t('send_upload_quote')}
						forceRequired={canChange.includes('upload_docs') && !(sendData.enabled ? validation.send_docs : false)}
						mustComplete={canChange}
						showIsOptional={true}
						text={getDocumentsCountLabel(uploadFiles)}
						isValid={validation.upload_docs && uploadFiles.length > 0}
					/>
				)}
				{canChange.includes('send_or_upload_docs') && (
					<OpportunityFunnelTab
						eventKey="send_or_upload_docs"
						title={userRole === RoleEnum.SALES_DEALER ? i18next.t('send_upload_quote') : i18next.t('send_upload_quotation')}
						showIsOptional={!isUploadSendDocRequired}
						forceRequired={isUploadSendDocRequired && !(sendData.enabled ? validation.send_docs : false)}
						mustComplete={canChange}
						text={getDocumentsCountLabel(uploadFiles)}
						isValid={uploadFiles.length > 0 && validation.upload_docs}
					/>
				)}
			</Nav>
			{loading
				? <CardPlaceholder />
				: (
					<div>
						<Card>
							<Card.Body className='pt-4'>

								{/* ACCOUNT CONTENT */}
								{activeKey === 'account' && (
									<AccountPanel
										account={account}
										funnelData={funnelData}
									/>
								)}

								{/* OPPORTUNITY CONTENT */}
								{activeKey === 'opportunity' && (
									<OpportunityPanel
										funnelData={funnelData}
										selectedOpportunityStage={selectedOpportunityStage}
										setSelectedOpportunityStage={(newStage) => {
											resetData()
											setSelectedOpportunityStage(newStage)
										}}
										validateFn={validateFn}
										validation={validation}
										cleanOpportunityValues={cleanOpportunityValues}
										setAccountModalOpened={setAccountModalOpened}
										setModalType={setModalType}
										accountModalOpened={accountModalOpened}
										opportunityValues={opportunityValues}
									/>
								)}

								{/* VEHICLE SECTION OPPORTUNITY */}
								{activeKey === 'vehicle' && (
									<SelectVehiclePanel
										funnelData={funnelData}
										setFunnelData={setFunnelData}
										setVehicleSearchOpt={setVehicleSearchOpt}
										vehicleSearchOpt={vehicleSearchOpt}
										vehicleSearch={vehicleSearch}
										selectedOpportunityStage={selectedOpportunityStage}
										opportunityValues={opportunityValues}
										setOpportunityValues={setOpportunityValues}
										activateDriveItEasy={activateDriveItEasy}
										setActivateDriveItEasy={setActivateDriveItEasy}
									/>
								)}

								{/* SEND DOCS CONTENT */}
								{activeKey === 'send_docs' && (
									<SendDocumentsPanel
										funnelData={funnelData}
										sendData={sendData}
										mustComplete={canChange}
										setDealerFolderData={setDealerFolderData}
										setSendData={setSendData}
										documentationFolder={documentationFolder}
										loadDealerDocs={loadDealerDocs}
										dealerFolderData={dealerFolderData}
										sendFiles={sendFiles}
										setSendFiles={setSendFiles}
									/>
								)}

								{/* UPLOAD DOCS CONTENT */}
								{activeKey === 'upload_docs' && (
									<UploadDocumentsPanel
										uploadFiles={uploadFiles}
										setUploadFiles={setUploadFiles}
									/>
								)}

								{/* UPLOAD DOCS CONTENT */}
								{activeKey === 'send_or_upload_docs' && (
									<UploadSendDocumentsPanel
										funnelData={funnelData}
										sendEmailData={sendEmailData}
										setSendEmailData={setSendEmailData}
										uploadFiles={uploadFiles}
										setUploadFiles={setUploadFiles}
									/>
								)}

							</Card.Body>
						</Card>

						<div className="mt-4 text-end">
							<div>
								<Button className="mx-4" onClick={onQuit} disabled={sending}>{t('close')}</Button>
								<Button variant='primary' onClick={() => submitFn()} disabled={!canSave || sending}>
									{sending ? <Spinner animation='border' size='sm' /> : sendData.enabled ? i18next.t('save_and_send') : i18next.t('save')}
								</Button>
							</div>
							<div>
								<hr />
								<small className="text-muted">
									<FaInfoCircle /> {t('opportunity_saved_as')} <b>"{translateOpportunityState(OPPORTUNITY_STATES_DATA[selectedOpportunityStage].label)}"</b>
								</small>
							</div>
						</div>
					</div>
				)}

			<OpportunityModals
				validation={validation}
				modalType={modalType}
				setAccountSearchOpt={() => { }} // not needed in this use case
				setAccountModalOpened={setAccountModalOpened}
				setFunnelData={setFunnelData}
				funnelData={funnelData}
				show={accountModalOpened}
				onHide={hideModal}
				account={account}
				setOpportunityValues={setOpportunityValues}
				opportunityValues={opportunityValues}
				opportunityFields={opportunityFields}
				opportunityValidationSchema={opportunityCreationSchema}
			/>
		</div >
	);
};
